import React, { useState, useEffect, useCallback } from 'react';
import { Search, Clock, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, Activity, CheckCircle2 } from 'lucide-react';
import * as api from '../services/api';

// Reuse the same badge pattern as LogList for visual consistency
const LevelBadge = ({ level }) => {
  const levelClass = `level-${level?.toLowerCase()}`;
  return <span className={`badge ${levelClass}`}>{level}</span>;
};

const Analytics = ({ selectedApp, workerApiUrl }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [timeRange, setTimeRange] = useState('24h');

  // Server-side accurate totals (from COUNT queries, not capped by fetch limit)
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalErrors, setTotalErrors] = useState(0);
  const [totalLogsLastPeriod, setTotalLogsLastPeriod] = useState(0);
  const [totalErrorsLastPeriod, setTotalErrorsLastPeriod] = useState(0);

  // Client-side aggregated from fetched logs
  const [logsByTemplate, setLogsByTemplate] = useState([]);
  const [levelCounts, setLevelCounts] = useState({});
  const [chartData, setChartData] = useState([]);

  /**
   * Aggregate fetched logs into chart buckets and a level+template breakdown.
   * Accepts `currentTimeRange` explicitly so it works correctly when called
   * inside `fetchAnalytics` (avoids stale-closure issues with `timeRange`).
   */
  const processAnalytics = (logs, currentTimeRange) => {
    const templateLevelMap = new Map();
    const timeBuckets = {};
    const levelMap = {};

    logs.forEach(log => {
      // Use template_name (human-readable) not template_id (UUID)
      const templateName = log.template_name || 'Unknown Template';
      const level = log.level?.toLowerCase() || 'unknown';
      const key = `${level}::${templateName}`;

      if (!templateLevelMap.has(key)) {
        templateLevelMap.set(key, { templateName, level, count: 0 });
      }
      templateLevelMap.get(key).count += 1;

      levelMap[level] = (levelMap[level] || 0) + 1;

      // --- Time bucketing ---
      // 24h: bucket = hour of day (0-23)
      // 7d:  bucket = days ago from today (0 = today, 6 = 6 days ago)
      const logDate = new Date(log.timestamp);
      let bucketKey;

      if (currentTimeRange === '24h') {
        bucketKey = logDate.getHours();
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const logDay = new Date(logDate);
        logDay.setHours(0, 0, 0, 0);
        bucketKey = Math.round((today.getTime() - logDay.getTime()) / (24 * 60 * 60 * 1000));
      }

      if (!timeBuckets[bucketKey]) {
        timeBuckets[bucketKey] = { error: 0, fatal: 0, warn: 0, info: 0, debug: 0, total: 0 };
      }
      timeBuckets[bucketKey][level] = (timeBuckets[bucketKey][level] || 0) + 1;
      timeBuckets[bucketKey].total += 1;
    });

    setLogsByTemplate(
      Array.from(templateLevelMap.values()).sort((a, b) => b.count - a.count)
    );
    setLevelCounts(levelMap);

    if (currentTimeRange === '24h') {
      // 24 hourly buckets (00:00 … 23:00)
      setChartData(
        Array.from({ length: 24 }, (_, i) => {
          const bucket = timeBuckets[i] || {};
          return {
            label: `${String(i).padStart(2, '0')}:00`,
            errorCount: (bucket.error || 0) + (bucket.fatal || 0),
            warnCount: bucket.warn || 0,
            totalCount: bucket.total || 0,
          };
        })
      );
    } else {
      // 7 daily buckets: i=0 is 6 days ago, i=6 is today
      setChartData(
        Array.from({ length: 7 }, (_, i) => {
          const daysAgo = 6 - i;
          const d = new Date();
          d.setDate(d.getDate() - daysAgo);
          const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          const bucket = timeBuckets[daysAgo] || {};
          return {
            label,
            errorCount: (bucket.error || 0) + (bucket.fatal || 0),
            warnCount: bucket.warn || 0,
            totalCount: bucket.total || 0,
          };
        })
      );
    }
  };

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    const now = new Date();
    const fromDate = new Date();
    if (timeRange === '24h') {
      fromDate.setHours(now.getHours() - 24);
    } else if (timeRange === '7d') {
      fromDate.setDate(now.getDate() - 7);
    }

    try {
      // Fetch up to 500 logs so chart data is representative.
      // Server-side totalLogs / totalErrors are accurate COUNT queries regardless of this limit.
      const data = await api.getLogs(workerApiUrl, {
        appId: selectedApp.id,
        limit: 500,
        from: fromDate.toISOString(),
        to: now.toISOString(),
        timeRange,
      });

      setTotalLogs(data.totalLogs);
      setTotalErrors(data.totalErrors);
      setTotalLogsLastPeriod(data.totalLogsLastPeriod);
      setTotalErrorsLastPeriod(data.totalErrorsLastPeriod);
      processAnalytics(data.logs, timeRange);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workerApiUrl, selectedApp.id, timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // --- Derived values ---
  const filteredEntries = logsByTemplate.filter(entry =>
    entry.templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.level.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const maxChartValue = Math.max(...chartData.map(d => d.totalCount), 1);

  const successRate = totalLogs > 0
    ? (((totalLogs - totalErrors) / totalLogs) * 100).toFixed(1)
    : 0;

  const errorChange = totalErrorsLastPeriod > 0
    ? (((totalErrors - totalErrorsLastPeriod) / totalErrorsLastPeriod) * 100).toFixed(1)
    : (totalErrors > 0 ? 100 : 0);

  const successRateLastPeriod = totalLogsLastPeriod > 0
    ? (((totalLogsLastPeriod - totalErrorsLastPeriod) / totalLogsLastPeriod) * 100).toFixed(1)
    : 0;

  const successRateChange = successRateLastPeriod > 0
    ? ((successRate - successRateLastPeriod) / successRateLastPeriod * 100).toFixed(1)
    : (successRate > 0 ? 100 : 0);

  const errorChangeClass = errorChange > 0 ? 'negative' : (errorChange < 0 ? 'positive' : 'neutral');
  const successRateChangeClass = successRateChange > 0 ? 'positive' : (successRateChange < 0 ? 'negative' : 'neutral');

  const ChangeArrow = ({ change }) => {
    if (change > 0) return <TrendingUp size={14} />;
    if (change < 0) return <TrendingDown size={14} />;
    return null;
  };

  // Bar colour: red if any errors/fatals, amber if only warnings, blue if clean
  const getBarColor = (item) => {
    if (item.errorCount > 0) return '#ef4444';
    if (item.warnCount > 0) return '#f59e0b';
    if (item.totalCount > 0) return '#3b82f6';
    return 'var(--border-color)';
  };

  const periodLabel = timeRange === '24h' ? 'day' : 'week';

  return (
    <div className="analytics-view">

      {/* ── Filter Controls ──────────────────────────────────────── */}
      <div className="analytics-controls">
        <button
          className={`filter-btn ${timeRange === '24h' ? 'active' : ''}`}
          onClick={() => setTimeRange('24h')}
        >
          <Clock className='btn-icon' size={16} />
          24 <span className="btn-text">hours</span>
        </button>
        <button
          className={`filter-btn ${timeRange === '7d' ? 'active' : ''}`}
          onClick={() => setTimeRange('7d')}
        >
          <Clock className='btn-icon' size={16} />
          7 <span className="btn-text">days</span>
        </button>
        <button className="refresh-btn" onClick={fetchAnalytics} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
        </button>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div className="analytics-summary">
        <div className="summary-card">
          <div className="summary-icon error">
            <AlertTriangle size={24} />
          </div>
          <div className="summary-content">
            <h3>Total Errors</h3>
            <p className="summary-value">{loading ? '—' : totalErrors.toLocaleString()}</p>
            <span className={`summary-change ${errorChangeClass}`}>
              <ChangeArrow change={Number(errorChange)} />
              {Math.abs(errorChange)}% from previous {periodLabel}
            </span>
          </div>
        </div>

        <div className="summary-card">
          {/* CheckCircle2, not TrendingDown — a success metric should never show a downward icon */}
          <div className="summary-icon success">
            <CheckCircle2 size={24} />
          </div>
          <div className="summary-content">
            <h3>Success Rate</h3>
            <p className="summary-value">{loading ? '—' : `${successRate}%`}</p>
            <span className={`summary-change ${successRateChangeClass}`}>
              <ChangeArrow change={Number(successRateChange)} />
              {Math.abs(successRateChange)}% from previous {periodLabel}
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon info">
            <Activity size={24} />
          </div>
          <div className="summary-content">
            <h3>Total Events</h3>
            <p className="summary-value">{loading ? '—' : totalLogs.toLocaleString()}</p>
            <span className="summary-change neutral">Last {timeRange}</span>
          </div>
        </div>
      </div>

      {/* ── Level Distribution ────────────────────────────────────── */}
      {!loading && Object.keys(levelCounts).length > 0 && (
        <div className="level-distribution">
          {['fatal', 'error', 'warn', 'info', 'debug']
            .filter(l => levelCounts[l])
            .map(level => (
              <div key={level} className="level-dist-item">
                <LevelBadge level={level} />
                <span className="level-dist-count">{levelCounts[level].toLocaleString()}</span>
              </div>
            ))
          }
        </div>
      )}

      {/* ── Log Volume Chart ──────────────────────────────────────── */}
      <div className="analytics-chart">
        <div className="chart-header">
          <h3>Log Volume — {timeRange === '24h' ? 'Last 24 Hours (by hour)' : 'Last 7 Days'}</h3>
          <div className="chart-legend">
            <span className="chart-legend-item legend-error">Errors / Fatal</span>
            <span className="chart-legend-item legend-warn">Warnings</span>
            <span className="chart-legend-item legend-ok">Info / Debug</span>
          </div>
        </div>
        <div className="chart-container">
          <div className="chart-bars">
            {chartData.map((item, index) => (
              <div key={index} className="chart-bar-wrapper">
                <div
                  className="chart-bar"
                  style={{
                    height: `${(item.totalCount / maxChartValue) * 100}%`,
                    minHeight: item.totalCount > 0 ? '4px' : '0px',
                    background: getBarColor(item),
                  }}
                  title={`${item.label}: ${item.totalCount} total (${item.errorCount} errors, ${item.warnCount} warnings)`}
                />
                <span className="chart-label">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Log Activity Table ────────────────────────────────────── */}
      <div className="analytics-table-container">
        <div className="table-toolbar">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by template or level..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container-inner">
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Template</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="3">Loading analytics...</td></tr>
              ) : error ? (
                <tr><td colSpan="3" className="error">{error}</td></tr>
              ) : filteredEntries.length > 0 ? (
                filteredEntries.map((entry, idx) => (
                  <tr key={`${entry.level}-${entry.templateName}-${idx}`}>
                    <td><LevelBadge level={entry.level} /></td>
                    <td className="log-template">
                      <span className="template-name" title={entry.templateName}>
                        {entry.templateName}
                      </span>
                    </td>
                    <td className="requests-count">{entry.count.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="3">No activity found for this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Analytics;


